// REACT IMPORTS
import React, { useContext, useEffect, useMemo, useState } from "react";

// CSS
import "./AccessLogsDashboard.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// DAYJS
import dayjs from "dayjs";

// API
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import {
	Checkbox,
	CircularProgress,
	Collapse,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	ListItemText,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
} from "@mui/material";

// MUI ICONS
import RefreshIcon from "@mui/icons-material/Refresh";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ViewListIcon from "@mui/icons-material/ViewList";
import BarChartIcon from "@mui/icons-material/BarChart";
import GroupIcon from "@mui/icons-material/Group";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import AccessLog from "../AccessLog/AccessLog";

// CONSTANTS
import { defaultBlue, defaultOrange, selectedBlue } from "../../../constants/theme";
import { convertToPST } from "../../../constants/dateTime";

// CONSTANTS - local to this dashboard
const DANGER_COLOR = "#B53943";
const DANGER_BG = "#FBEEEF";
const MUTED_COLOR = "#6B6B6B";
const DAY_MS = 86400000;
const RANGE_PRESETS = [
	{ value: 7, label: "7d" },
	{ value: 30, label: "30d" },
	{ value: 90, label: "90d" },
];
const MONTHS = [
	"Jan", "Feb", "Mar", "Apr", "May", "Jun",
	"Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// HELPERS
const fmtNum = (n) => n.toLocaleString("en-US");

const initialsOf = (name) =>
	(name || "?")
		.split(" ")
		.filter(Boolean)
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

const dayKey = (ts) => dayjs(ts).format("YYYY-MM-DD");

const axisLabel = (key) => {
	const d = dayjs(key);
	return `${MONTHS[d.month()]} ${d.date()}`;
};

const lastSeenLabel = (ts) => {
	if (!ts) return "Never";
	const days = dayjs().diff(dayjs(ts), "day");
	if (days <= 0) return "Today";
	if (days === 1) return "Yesterday";
	if (days < 30) return `${days} days ago`;
	const months = Math.floor(days / 30);
	return months === 1 ? "1 month ago" : `${months} months ago`;
};

const hour12 = (h) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? " AM" : " PM"}`;

// REACT COMPONENT
const AccessLogsDashboard = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// DATA STATE
	const [rows, setRows] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [lastUpdated, setLastUpdated] = useState(null);

	// CONTROL STATE
	const [view, setView] = useState("dashboard");
	// Bumped by the header's reload button so the embedded full list (which
	// fetches its own data) refetches too, regardless of which view is active.
	const [listRefreshTrigger, setListRefreshTrigger] = useState(0);
	const [tab, setTab] = useState("overview");

	const [preset, setPreset] = useState(30);
	const [startDate, setStartDate] = useState(
		dayjs().subtract(29, "day").format("YYYY-MM-DD")
	);
	const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));

	// Empty array = all pages / all employees.
	const [pageFilter, setPageFilter] = useState([]);
	const [employeeFilter, setEmployeeFilter] = useState([]);

	const [hoverDay, setHoverDay] = useState(null);

	const [sortPages, setSortPages] = useState({ key: "count", dir: "desc" });
	const [sortUsers, setSortUsers] = useState({ key: "count", dir: "desc" });

	const [expandedPage, setExpandedPage] = useState(null);
	const [expandedUser, setExpandedUser] = useState(null);

	const [logDialog, setLogDialog] = useState(null);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Access Logs Dashboard",
			employeeID: employeeID,
			notes: "Accessed the Access Logs Dashboard page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		fetchDashboard();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// API HELPER FUNCTIONS
	const fetchDashboard = async () => {
		setIsLoading(true);

		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/logs/access/dashboard"
			);
			setRows(response.data.rows);
			setEmployees(response.data.employees);
			setLastUpdated(Date.now());
		} catch (error) {
			console.error(error);
		}

		setIsLoading(false);
	};

	// RANGE HANDLING
	const applyPreset = (value) => {
		const end = dayjs();
		setPreset(value);
		setStartDate(end.subtract(value - 1, "day").format("YYYY-MM-DD"));
		setEndDate(end.format("YYYY-MM-DD"));
	};

	const handleResetFilters = () => {
		setPageFilter([]);
		setEmployeeFilter([]);
		applyPreset(30);
	};

	// The header's reload button both refetches from the server and clears
	// filters back to the default 30-day range - there's no separate reset
	// control in the filter bar itself.
	const handleRefresh = () => {
		fetchDashboard();
		setListRefreshTrigger((prev) => prev + 1);
		handleResetFilters();
	};

	// DERIVED DATA - range + options
	const rangeStart = useMemo(() => dayjs(`${startDate}T00:00:00`), [startDate]);
	const rangeEnd = useMemo(() => dayjs(`${endDate}T23:59:59`), [endDate]);

	const pageOptions = useMemo(
		() =>
			Array.from(
				new Set(rows.map((r) => r.page_name).filter(Boolean))
			).sort(),
		[rows]
	);

	const employeeOptions = useMemo(
		() => employees.slice().sort((a, b) => a.name.localeCompare(b.name)),
		[employees]
	);

	// Internally, an empty filter array means "no restriction" (includes rows
	// from employees/pages outside the current option list, e.g. a terminated
	// employee's old log entries). But the multi-select should still LOOK
	// like everything is checked by default, so these derive what the Select
	// actually renders/receives without changing that underlying semantics -
	// only an explicit, user-driven deselection turns into a real filter.
	const allPageIds = pageOptions;
	const pageFilterValue = pageFilter.length === 0 ? allPageIds : pageFilter;

	const allEmployeeIds = useMemo(
		() => employeeOptions.map((opt) => String(opt.id)),
		[employeeOptions]
	);
	const employeeFilterValue =
		employeeFilter.length === 0 ? allEmployeeIds : employeeFilter;

	// Every access ever logged for an employee, ignoring the selected date
	// range - lets "last seen" stay accurate even for employees the current
	// range filters out entirely.
	const lastEverMap = useMemo(() => {
		const map = {};
		rows.forEach((r) => {
			if (r.employee_id == null) return;
			if (!map[r.employee_id] || dayjs(r.created).isAfter(map[r.employee_id])) {
				map[r.employee_id] = r.created;
			}
		});
		return map;
	}, [rows]);

	const employeeById = useMemo(() => {
		const map = {};
		employees.forEach((e) => {
			map[e.id] = e;
		});
		return map;
	}, [employees]);

	// DERIVED DATA - filtered rows for the selected range/page/employee
	const baseRows = useMemo(() => {
		return rows.filter((r) => {
			const ts = dayjs(r.created);
			if (ts.isBefore(rangeStart) || ts.isAfter(rangeEnd)) return false;
			if (pageFilter.length > 0 && !pageFilter.includes(r.page_name)) {
				return false;
			}
			if (
				employeeFilter.length > 0 &&
				!employeeFilter.includes(String(r.employee_id))
			) {
				return false;
			}
			return true;
		});
	}, [rows, rangeStart, rangeEnd, pageFilter, employeeFilter]);

	// Fallback names for log rows whose employee is no longer in the active
	// roster (terminated) - so drill-downs don't just show a bare ID.
	const rowEmployeeNameById = useMemo(() => {
		const map = {};
		baseRows.forEach((r) => {
			if (r.employee_id != null && !map[r.employee_id]) {
				map[r.employee_id] = r.employee_name || `Employee #${r.employee_id}`;
			}
		});
		return map;
	}, [baseRows]);

	const pageList = useMemo(() => {
		const map = {};
		baseRows.forEach((r) => {
			const name = r.page_name || "Unknown";
			const entry =
				map[name] ||
				(map[name] = {
					name,
					count: 0,
					users: new Set(),
					last: null,
					userCounts: {},
					userLast: {},
				});
			entry.count += 1;
			if (r.employee_id != null) {
				entry.users.add(r.employee_id);
				entry.userCounts[r.employee_id] =
					(entry.userCounts[r.employee_id] || 0) + 1;
				if (
					!entry.userLast[r.employee_id] ||
					dayjs(r.created).isAfter(entry.userLast[r.employee_id])
				) {
					entry.userLast[r.employee_id] = r.created;
				}
			}
			if (!entry.last || dayjs(r.created).isAfter(entry.last)) {
				entry.last = r.created;
			}
		});
		return Object.values(map);
	}, [baseRows]);

	const maxPageCount = Math.max(1, ...pageList.map((p) => p.count), 0);

	const userStatsById = useMemo(() => {
		const map = {};
		employees.forEach((e) => {
			map[e.id] = { count: 0, pages: {}, pageLast: {} };
		});
		baseRows.forEach((r) => {
			const m = map[r.employee_id];
			if (!m) return;
			m.count += 1;
			const page = r.page_name || "Unknown";
			m.pages[page] = (m.pages[page] || 0) + 1;
			if (!m.pageLast[page] || dayjs(r.created).isAfter(m.pageLast[page])) {
				m.pageLast[page] = r.created;
			}
		});
		return map;
	}, [employees, baseRows]);

	const total = baseRows.length;
	const activeEmployees = employees.filter(
		(e) => userStatsById[e.id]?.count > 0
	);
	const inactiveEmployees = employees.filter(
		(e) => !userStatsById[e.id] || userStatsById[e.id].count === 0
	);

	// DERIVED DATA - KPIs
	const dayCount = Math.max(
		1,
		Math.round((rangeEnd.valueOf() - rangeStart.valueOf()) / DAY_MS)
	);
	const prevStart = rangeStart.subtract(dayCount, "day");
	const prevRowCount = useMemo(
		() =>
			rows.filter((r) => {
				const ts = dayjs(r.created);
				return (
					(ts.isAfter(prevStart) || ts.isSame(prevStart)) &&
					ts.isBefore(rangeStart)
				);
			}).length,
		[rows, prevStart, rangeStart]
	);
	const delta = prevRowCount
		? Math.round(((total - prevRowCount) / prevRowCount) * 100)
		: 0;

	const topPage = pageList.slice().sort((a, b) => b.count - a.count)[0];

	const byHour = useMemo(() => {
		const arr = new Array(24).fill(0);
		baseRows.forEach((r) => {
			arr[dayjs(r.created).hour()] += 1;
		});
		return arr;
	}, [baseRows]);
	let peakHour = 0;
	byHour.forEach((v, i) => {
		if (v > byHour[peakHour]) peakHour = i;
	});

	const kpis = [
		{
			label: "Total Accesses",
			value: fmtNum(total),
			sub: `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta)}% vs prior ${dayCount} ${
				dayCount === 1 ? "day" : "days"
			}`,
			subColor: delta >= 0 ? "#568C5B" : DANGER_COLOR,
			icon: BarChartIcon,
			tint: defaultBlue,
		},
		{
			label: "Active Users",
			value: `${activeEmployees.length} / ${employees.length}`,
			sub: employees.length
				? `${Math.round(
						(activeEmployees.length / employees.length) * 100
				  )}% of roster`
				: "no roster loaded",
			subColor: MUTED_COLOR,
			icon: GroupIcon,
			tint: defaultBlue,
		},
		{
			label: "Inactive Users",
			value: String(inactiveEmployees.length),
			sub: "no access this period",
			subColor: DANGER_COLOR,
			icon: PersonOffIcon,
			tint: DANGER_COLOR,
		},
		{
			label: "Busiest Page",
			value: topPage && topPage.count ? topPage.name : "—",
			sub:
				topPage && topPage.count
					? `${fmtNum(topPage.count)} views · ${Math.round(
							(topPage.count / (total || 1)) * 100
					  )}% of traffic`
					: "no data",
			subColor: MUTED_COLOR,
			icon: TrendingUpIcon,
			tint: defaultOrange,
		},
		{
			label: "Peak Hour",
			value: total ? hour12(peakHour) : "—",
			sub: total
				? `${fmtNum(byHour[peakHour])} views in this hour`
				: "no data",
			subColor: MUTED_COLOR,
			icon: ScheduleIcon,
			tint: "#6D7E8C",
		},
	];

	// DERIVED DATA - timeline
	const dayKeys = useMemo(() => {
		const keys = [];
		let cursor = rangeStart.startOf("day");
		const last = rangeEnd.startOf("day");
		// Bound the axis to a sane number of bars even if "All time" spans years.
		let guard = 0;
		while ((cursor.isBefore(last) || cursor.isSame(last)) && guard < 1000) {
			keys.push(cursor.format("YYYY-MM-DD"));
			cursor = cursor.add(1, "day");
			guard += 1;
		}
		return keys;
	}, [rangeStart, rangeEnd]);

	const { perDay, perDayUsers } = useMemo(() => {
		const perDayMap = {};
		const perDayUsersMap = {};
		dayKeys.forEach((k) => {
			perDayMap[k] = 0;
			perDayUsersMap[k] = new Set();
		});
		baseRows.forEach((r) => {
			const k = dayKey(r.created);
			if (perDayMap[k] === undefined) return;
			perDayMap[k] += 1;
			if (r.employee_id != null) perDayUsersMap[k].add(r.employee_id);
		});
		return { perDay: perDayMap, perDayUsers: perDayUsersMap };
	}, [dayKeys, baseRows]);

	const maxDay = Math.max(1, ...dayKeys.map((k) => perDay[k] || 0), 0);

	const hoverInfo =
		hoverDay != null && perDay[hoverDay] !== undefined
			? {
					date: dayjs(hoverDay).format("ddd, MMM D"),
					views: `${fmtNum(perDay[hoverDay])} ${
						perDay[hoverDay] === 1 ? "visit" : "visits"
					}`,
					users: `${fmtNum(perDayUsers[hoverDay].size)} ${
						perDayUsers[hoverDay].size === 1
							? "unique visitor"
							: "unique visitors"
					}`,
					pct:
						dayKeys.length > 1
							? (dayKeys.indexOf(hoverDay) / (dayKeys.length - 1)) * 100
							: 50,
			  }
			: null;

	// DERIVED DATA - top pages + inactive users (overview panels)
	const topPages = pageList.slice().sort((a, b) => b.count - a.count).slice(0, 6);

	const inactiveUsersSorted = inactiveEmployees.slice().sort((a, b) => {
		const at = lastEverMap[a.id] ? dayjs(lastEverMap[a.id]).valueOf() : 0;
		const bt = lastEverMap[b.id] ? dayjs(lastEverMap[b.id]).valueOf() : 0;
		return bt - at;
	});

	// DERIVED DATA - pages tab
	const handleSortPages = (key) =>
		setSortPages((prev) => ({
			key,
			dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc",
		}));

	const pageRows = useMemo(() => {
		return pageList.slice().sort((a, b) => {
			let v = 0;
			if (sortPages.key === "name") v = a.name.localeCompare(b.name);
			else if (sortPages.key === "users") v = a.users.size - b.users.size;
			else if (sortPages.key === "last") {
				v = dayjs(a.last).valueOf() - dayjs(b.last).valueOf();
			} else v = a.count - b.count;
			return sortPages.dir === "asc" ? v : -v;
		});
	}, [pageList, sortPages]);

	// DERIVED DATA - users tab
	const handleSortUsers = (key) =>
		setSortUsers((prev) => ({
			key,
			dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc",
		}));

	const userRows = useMemo(() => {
		return employees
			.slice()
			.sort((a, b) => {
				const sa = userStatsById[a.id] || { count: 0, pages: {} };
				const sb = userStatsById[b.id] || { count: 0, pages: {} };
				let v = 0;
				if (sortUsers.key === "name") v = a.name.localeCompare(b.name);
				else if (sortUsers.key === "pages") {
					v = Object.keys(sa.pages).length - Object.keys(sb.pages).length;
				} else if (sortUsers.key === "last") {
					const at = lastEverMap[a.id] ? dayjs(lastEverMap[a.id]).valueOf() : 0;
					const bt = lastEverMap[b.id] ? dayjs(lastEverMap[b.id]).valueOf() : 0;
					v = at - bt;
				} else v = sa.count - sb.count;
				return sortUsers.dir === "asc" ? v : -v;
			});
	}, [employees, userStatsById, sortUsers, lastEverMap]);

	// DERIVED DATA - log dialog (page x employee drill-down)
	const logDialogEntries = useMemo(() => {
		if (!logDialog) return [];
		return baseRows
			.filter(
				(r) =>
					r.page_name === logDialog.page &&
					String(r.employee_id) === String(logDialog.employeeId)
			)
			.sort((a, b) => dayjs(b.created).valueOf() - dayjs(a.created).valueOf());
	}, [baseRows, logDialog]);

	const logDialogEmployeeName = logDialog
		? employeeById[logDialog.employeeId]?.name ||
		  rowEmployeeNameById[logDialog.employeeId] ||
		  `Employee #${logDialog.employeeId}`
		: "";

	// RENDER
	return (
		<div className="AccessLogsDashboard">
			<div className="accessLogsDashboardHeader">
				<div className="accessLogsDashboardHeaderRow">
					<div className="accessLogsDashboardHeaderTitle">Access Logs</div>
					<div className="accessLogsDashboardHeaderActions">
						{lastUpdated && (
							<span className="accessLogsDashboardUpdatedText">
								Updated {convertToPST(lastUpdated)}
							</span>
						)}
						<Tooltip title="Refresh">
							<span>
								<IconButton
									onClick={handleRefresh}
									disabled={isLoading}
									size="small"
								>
									<RefreshIcon />
								</IconButton>
							</span>
						</Tooltip>
						<ToggleButtonGroup
							size="small"
							value={view}
							exclusive
							onChange={(e, value) => value && setView(value)}
							sx={{
								height: 36,
								"& .MuiToggleButton-root": {
									height: 36,
									boxSizing: "border-box",
									textTransform: "none",
									color: "text.secondary",
									borderColor: "#e0e0e0",
								},
								"& .MuiToggleButton-root.Mui-selected": {
									backgroundColor: "primary.main",
									color: "#fff",
								},
								"& .MuiToggleButton-root.Mui-selected:hover": {
									backgroundColor: "primary.main",
								},
							}}
						>
							<ToggleButton value="dashboard">
								<DashboardIcon fontSize="small" style={{ marginRight: 6 }} />
								Dashboard
							</ToggleButton>
							<ToggleButton value="list">
								<ViewListIcon fontSize="small" style={{ marginRight: 6 }} />
								Full List
							</ToggleButton>
						</ToggleButtonGroup>
					</div>
				</div>
			</div>

			{view === "list" && (
				<AccessLog embedded refreshTrigger={listRefreshTrigger} />
			)}

			{view === "dashboard" && isLoading && rows.length === 0 && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}

			{view === "dashboard" && !(isLoading && rows.length === 0) && (
				<>
					{/* Toolbar - pinned above the scrollable body: view tabs far
					    left, filters far right */}
					<div className="accessLogsDashboardToolbarRow">
						<div className="accessLogsDashboardTabs">
							{[
								{ key: "overview", label: "Overview" },
								{ key: "pages", label: "Pages" },
								{ key: "users", label: "Users" },
							].map((t) => (
								<div
									key={t.key}
									className="accessLogsDashboardTab"
									onClick={() => setTab(t.key)}
									style={{
										color: tab === t.key ? defaultBlue : MUTED_COLOR,
										borderBottomColor:
											tab === t.key ? defaultBlue : "transparent",
									}}
								>
									{t.label}
								</div>
							))}
						</div>

						<div className="accessLogsDashboardFilterBar">
							<ToggleButtonGroup
								size="small"
								value={preset}
								exclusive
								onChange={(e, value) => value && applyPreset(value)}
							>
								{RANGE_PRESETS.map((r) => (
									<ToggleButton key={r.value} value={r.value}>
										{r.label}
									</ToggleButton>
								))}
							</ToggleButtonGroup>
							<div className="accessLogsDashboardDateInputs">
								<TextField
									type="date"
									size="small"
									value={startDate}
									inputProps={{ max: endDate }}
									onChange={(e) => {
										const value = e.target.value;
										setStartDate(value);
										if (value > endDate) setEndDate(value);
										setPreset(null);
									}}
								/>
								<span>to</span>
								<TextField
									type="date"
									size="small"
									value={endDate}
									inputProps={{ min: startDate }}
									onChange={(e) => {
										const value = e.target.value;
										setEndDate(value);
										if (value < startDate) setStartDate(value);
										setPreset(null);
									}}
								/>
							</div>
							<TextField
								select
								size="small"
								label="Page"
								value={pageFilterValue}
								onChange={(e) => {
									const next = e.target.value;
									setPageFilter(
										next.length >= allPageIds.length ? [] : next
									);
								}}
								sx={{ minWidth: 180 }}
								SelectProps={{
									multiple: true,
									renderValue: (selected) =>
										selected.length >= allPageIds.length
											? "All pages"
											: selected.length === 1
											? selected[0]
											: `${selected.length} pages`,
								}}
							>
								{pageOptions.map((opt) => (
									<MenuItem key={opt} value={opt}>
										<Checkbox
											size="small"
											checked={pageFilterValue.includes(opt)}
										/>
										<ListItemText primary={opt} />
									</MenuItem>
								))}
							</TextField>
							<TextField
								select
								size="small"
								label="Employee"
								value={employeeFilterValue}
								onChange={(e) => {
									const next = e.target.value;
									setEmployeeFilter(
										next.length >= allEmployeeIds.length ? [] : next
									);
								}}
								sx={{ minWidth: 180 }}
								SelectProps={{
									multiple: true,
									renderValue: (selected) =>
										selected.length >= allEmployeeIds.length
											? "All employees"
											: selected.length === 1
											? employeeById[selected[0]]?.name || "1 employee"
											: `${selected.length} employees`,
								}}
							>
								{employeeOptions.map((opt) => (
									<MenuItem key={opt.id} value={String(opt.id)}>
										<Checkbox
											size="small"
											checked={employeeFilterValue.includes(String(opt.id))}
										/>
										<ListItemText primary={opt.name} />
									</MenuItem>
								))}
							</TextField>
							<div className="accessLogsDashboardCountLabel">
								{fmtNum(total)} log entries
							</div>
						</div>
					</div>

					<div className="accessLogsDashboardBody">
						{tab === "overview" && (
							<>
								<div className="accessLogsDashboardKpiGrid">
									{kpis.map((k) => {
										const KpiIcon = k.icon;
										return (
											<div className="accessLogsDashboardKpiCard" key={k.label}>
												<div className="accessLogsDashboardKpiLabel">
													<KpiIcon style={{ fontSize: 16, color: k.tint }} />
													{k.label}
												</div>
												<div className="accessLogsDashboardKpiValue">
													{k.value}
												</div>
												<div
													className="accessLogsDashboardKpiSub"
													style={{ color: k.subColor }}
												>
													{k.sub}
												</div>
											</div>
										);
									})}
								</div>

								<div className="accessLogsDashboardTimelineCard">
									<div className="accessLogsDashboardTimelineHeader">
										<div>
											<div className="accessLogsDashboardTimelineTitle">
												Access Timeline
											</div>
											<div className="accessLogsDashboardTimelineSub">
												{`${
													pageFilter.length === 0
														? "All pages"
														: pageFilter.length === 1
														? pageFilter[0]
														: `${pageFilter.length} pages`
												} · visits per day · peak ${fmtNum(maxDay)}`}
											</div>
										</div>
									</div>

									<div className="accessLogsDashboardTimelineChart">
										<div className="accessLogsDashboardTimelineBars">
											{dayKeys.map((k) => (
												<div
													key={k}
													className="accessLogsDashboardTimelineBarTrack"
													onMouseEnter={() => setHoverDay(k)}
													onMouseLeave={() => setHoverDay(null)}
												>
													<div
														className="accessLogsDashboardTimelineBar"
														style={{
															height: `${Math.round(
																((perDay[k] || 0) / maxDay) * 100
															)}%`,
															background:
																hoverDay === k
																	? defaultOrange
																	: perDay[k] === maxDay
																	? selectedBlue
																	: defaultBlue,
														}}
													/>
												</div>
											))}
										</div>
										<div className="accessLogsDashboardTimelineAxis">
											<span>{dayKeys.length ? axisLabel(dayKeys[0]) : ""}</span>
											<span>
												{dayKeys.length
													? axisLabel(dayKeys[Math.floor(dayKeys.length / 2)])
													: ""}
											</span>
											<span>
												{dayKeys.length
													? axisLabel(dayKeys[dayKeys.length - 1])
													: ""}
											</span>
										</div>
										{hoverInfo && (
											<div
												className="accessLogsDashboardTimelineTooltip"
												style={{
													left: `${Math.min(94, Math.max(6, hoverInfo.pct))}%`,
												}}
											>
												<div className="accessLogsDashboardTimelineTooltipDate">
													{hoverInfo.date}
												</div>
												<div className="accessLogsDashboardTimelineTooltipLine">
													{hoverInfo.views}
												</div>
												<div className="accessLogsDashboardTimelineTooltipLineMuted">
													{hoverInfo.users}
												</div>
											</div>
										)}
									</div>
								</div>

								<div className="accessLogsDashboardGrid2">
									<div className="accessLogsDashboardTopPagesCard">
										<div className="accessLogsDashboardCardTitle">
											Most Accessed Pages
										</div>
										<div className="accessLogsDashboardTopPagesList">
											{topPages.length === 0 && <J2NoResultsFound />}
											{topPages.map((p) => (
												<div className="accessLogsDashboardTopPagesRow" key={p.name}>
													<div className="accessLogsDashboardTopPagesRowHeader">
														<div className="accessLogsDashboardTopPagesRowName">
															{p.name}
														</div>
														<div className="accessLogsDashboardTopPagesRowCount">
															{fmtNum(p.count)} views
														</div>
													</div>
													<div className="accessLogsDashboardTopPagesBarTrack">
														<div
															className="accessLogsDashboardTopPagesBarFill"
															style={{
																width: `${Math.round(
																	(p.count / maxPageCount) * 100
																)}%`,
															}}
														/>
													</div>
													<div className="accessLogsDashboardTopPagesRowUsers">
														{p.users.size} of {employees.length} employees
													</div>
												</div>
											))}
										</div>
									</div>

									<div className="accessLogsDashboardInactiveCard">
										<div className="accessLogsDashboardInactiveHeader">
											<PersonOffIcon
												style={{ color: DANGER_COLOR, fontSize: 20 }}
											/>
											<div className="accessLogsDashboardCardTitle">
												Inactive Users
											</div>
										</div>
										<div className="accessLogsDashboardInactiveSub">
											No recorded access in the selected range
										</div>
										<div className="accessLogsDashboardInactiveList">
											{inactiveUsersSorted.length === 0 && (
												<div className="accessLogsDashboardInactiveEmpty">
													Every active employee has accessed something in
													this range.
												</div>
											)}
											{inactiveUsersSorted.map((e) => (
												<div
													className="accessLogsDashboardInactiveRow"
													key={e.id}
												>
													<div className="accessLogsDashboardAvatar">
														{initialsOf(e.name)}
													</div>
													<div className="accessLogsDashboardInactiveRowInfo">
														<div className="accessLogsDashboardInactiveRowName">
															{e.name}
														</div>
														<div className="accessLogsDashboardInactiveRowRole">
															{e.role || "—"}
														</div>
													</div>
													<div className="accessLogsDashboardInactiveRowLast">
														<div
															style={{
																color: lastEverMap[e.id]
																	? MUTED_COLOR
																	: DANGER_COLOR,
															}}
														>
															{lastSeenLabel(lastEverMap[e.id])}
														</div>
														<div className="accessLogsDashboardInactiveRowLastLabel">
															last seen
														</div>
													</div>
												</div>
											))}
										</div>
										<div className="accessLogsDashboardInactiveFooter">
											{inactiveEmployees.length} of {employees.length} employees
											had no recorded access.
										</div>
									</div>
								</div>
							</>
						)}

						{tab === "pages" && (
							<TableContainer className="accessLogsDashboardTableContainer">
								<Table stickyHeader size="small">
									<TableHead>
										<TableRow>
											<TableCell>
												<TableSortLabel
													active={sortPages.key === "name"}
													direction={sortPages.dir}
													onClick={() => handleSortPages("name")}
												>
													Page
												</TableSortLabel>
											</TableCell>
											<TableCell align="right">
												<TableSortLabel
													active={sortPages.key === "count"}
													direction={sortPages.dir}
													onClick={() => handleSortPages("count")}
												>
													Views
												</TableSortLabel>
											</TableCell>
											<TableCell align="right">
												<TableSortLabel
													active={sortPages.key === "users"}
													direction={sortPages.dir}
													onClick={() => handleSortPages("users")}
												>
													Users
												</TableSortLabel>
											</TableCell>
											<TableCell align="right">
												<TableSortLabel
													active={sortPages.key === "last"}
													direction={sortPages.dir}
													onClick={() => handleSortPages("last")}
												>
													Last access
												</TableSortLabel>
											</TableCell>
											<TableCell />
										</TableRow>
									</TableHead>
									<TableBody>
										{pageRows.length === 0 && (
											<TableRow>
												<TableCell colSpan={5}>
													<J2NoResultsFound />
												</TableCell>
											</TableRow>
										)}
										{pageRows.map((p) => {
											const expanded = expandedPage === p.name;
											const userBreak = Object.keys(p.userCounts)
												.sort((a, b) => p.userCounts[b] - p.userCounts[a])
												.map((uid) => {
													const emp = employeeById[uid];
													return {
														uid,
														name:
															emp?.name ||
															rowEmployeeNameById[uid] ||
															`Employee #${uid}`,
														role: emp?.role || "Former employee",
														count: p.userCounts[uid],
														last: p.userLast[uid],
													};
												});
											return (
												<React.Fragment key={p.name}>
													<TableRow
														hover
														className="accessLogsDashboardRow"
														onClick={() =>
															setExpandedPage(expanded ? null : p.name)
														}
													>
														<TableCell>
															<div className="accessLogsDashboardNameCell">
																{expanded ? (
																	<KeyboardArrowUpIcon
																		style={{ color: "#b5b5b5" }}
																	/>
																) : (
																	<KeyboardArrowDownIcon
																		style={{ color: "#b5b5b5" }}
																	/>
																)}
																{p.name}
															</div>
														</TableCell>
														<TableCell align="right">
															{fmtNum(p.count)}
														</TableCell>
														<TableCell align="right">{p.users.size}</TableCell>
														<TableCell align="right">
															{p.last ? convertToPST(p.last) : "—"}
														</TableCell>
														<TableCell />
													</TableRow>
													<TableRow>
														<TableCell
															colSpan={5}
															style={{ paddingTop: 0, paddingBottom: 0 }}
														>
															<Collapse in={expanded} timeout="auto" unmountOnExit>
																<div className="accessLogsDashboardCleanExpandedPanel">
																	{userBreak.length === 0 && (
																		<div className="accessLogsDashboardExpandedEmpty">
																			No access recorded in this range.
																		</div>
																	)}
																	{userBreak.map((u) => (
																		<div
																			className="accessLogsDashboardExpandedRow"
																			key={u.uid}
																			onClick={() =>
																				setLogDialog({
																					page: p.name,
																					employeeId: u.uid,
																				})
																			}
																		>
																			<div className="accessLogsDashboardAvatar small">
																				{initialsOf(u.name)}
																			</div>
																			<div className="accessLogsDashboardExpandedRowName">
																				{u.name}
																				<span className="accessLogsDashboardExpandedRowRole">
																					{u.role}
																				</span>
																			</div>
																			<div className="accessLogsDashboardExpandedRowCount">
																				{fmtNum(u.count)}
																			</div>
																			<div className="accessLogsDashboardExpandedRowLast">
																				{u.last ? convertToPST(u.last) : "—"}
																			</div>
																			<ChevronRightIcon
																				style={{ color: "#a8b2bd", fontSize: 16 }}
																			/>
																		</div>
																	))}
																</div>
															</Collapse>
														</TableCell>
													</TableRow>
												</React.Fragment>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
						)}

						{tab === "users" && (
							<TableContainer className="accessLogsDashboardTableContainer">
								<Table stickyHeader size="small">
									<TableHead>
										<TableRow>
											<TableCell>
												<TableSortLabel
													active={sortUsers.key === "name"}
													direction={sortUsers.dir}
													onClick={() => handleSortUsers("name")}
												>
													Employee
												</TableSortLabel>
											</TableCell>
											<TableCell align="right">
												<TableSortLabel
													active={sortUsers.key === "count"}
													direction={sortUsers.dir}
													onClick={() => handleSortUsers("count")}
												>
													Views
												</TableSortLabel>
											</TableCell>
											<TableCell align="right">
												<TableSortLabel
													active={sortUsers.key === "pages"}
													direction={sortUsers.dir}
													onClick={() => handleSortUsers("pages")}
												>
													Pages
												</TableSortLabel>
											</TableCell>
											<TableCell align="right">
												<TableSortLabel
													active={sortUsers.key === "last"}
													direction={sortUsers.dir}
													onClick={() => handleSortUsers("last")}
												>
													Last seen
												</TableSortLabel>
											</TableCell>
											<TableCell />
										</TableRow>
										</TableHead>
										<TableBody>
											{userRows.length === 0 && (
												<TableRow>
													<TableCell colSpan={5}>
														<J2NoResultsFound />
													</TableCell>
												</TableRow>
											)}
											{userRows.map((e) => {
												const stats =
													userStatsById[e.id] || { count: 0, pages: {}, pageLast: {} };
												const dead = stats.count === 0;
												const expanded = expandedUser === e.id;
												const pageBreak = Object.keys(stats.pages)
													.sort((a, b) => stats.pages[b] - stats.pages[a])
													.map((name) => ({
														name,
														count: stats.pages[name],
														last: stats.pageLast[name],
													}));
												return (
													<React.Fragment key={e.id}>
														<TableRow
															hover
															className="accessLogsDashboardRow"
															onClick={() =>
																setExpandedUser(expanded ? null : e.id)
															}
														>
															<TableCell>
																<div className="accessLogsDashboardNameCell">
																	{expanded ? (
																		<KeyboardArrowUpIcon
																			style={{ color: "#b5b5b5" }}
																		/>
																	) : (
																		<KeyboardArrowDownIcon
																			style={{ color: "#b5b5b5" }}
																		/>
																	)}
																	<div
																		className="accessLogsDashboardAvatar"
																		style={{
																			background: dead ? DANGER_BG : "#e7eef7",
																			color: dead ? DANGER_COLOR : defaultBlue,
																		}}
																	>
																		{initialsOf(e.name)}
																	</div>
																	<div>
																		<div>{e.name}</div>
																		<div className="accessLogsDashboardMutedSmall">
																			{e.role || "—"}
																		</div>
																	</div>
																</div>
															</TableCell>
															<TableCell align="right">
																{fmtNum(stats.count)}
															</TableCell>
															<TableCell align="right">
																{Object.keys(stats.pages).length}
															</TableCell>
															<TableCell
																align="right"
																style={{ color: dead ? DANGER_COLOR : MUTED_COLOR }}
															>
																{lastSeenLabel(lastEverMap[e.id])}
															</TableCell>
															<TableCell />
														</TableRow>
														<TableRow>
															<TableCell
																colSpan={5}
																style={{ paddingTop: 0, paddingBottom: 0 }}
															>
																<Collapse in={expanded} timeout="auto" unmountOnExit>
																	<div className="accessLogsDashboardCleanExpandedPanel">
																		{pageBreak.length === 0 && (
																			<div className="accessLogsDashboardExpandedEmpty">
																				No access recorded in this range.
																			</div>
																		)}
																		{pageBreak.map((p) => (
																			<div
																				className="accessLogsDashboardExpandedRow"
																				key={p.name}
																				onClick={() =>
																					setLogDialog({
																						page: p.name,
																						employeeId: e.id,
																					})
																				}
																			>
																				<div className="accessLogsDashboardExpandedRowName">
																					{p.name}
																				</div>
																				<div className="accessLogsDashboardExpandedRowCount">
																					{fmtNum(p.count)}
																				</div>
																				<div className="accessLogsDashboardExpandedRowLast">
																					{p.last ? convertToPST(p.last) : "—"}
																				</div>
																				<ChevronRightIcon
																					style={{ color: "#a8b2bd", fontSize: 16 }}
																				/>
																			</div>
																		))}
																	</div>
																</Collapse>
															</TableCell>
														</TableRow>
													</React.Fragment>
												);
											})}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</div>
				</>
			)}

			<Dialog
				open={Boolean(logDialog)}
				onClose={() => setLogDialog(null)}
				fullWidth
				maxWidth="sm"
			>
				{logDialog && (
					<>
						<DialogTitle>
							{logDialogEmployeeName} · {logDialog.page}
						</DialogTitle>
						<DialogContent>
							<div className="accessLogsDashboardDialogSub">
								{fmtNum(logDialogEntries.length)}{" "}
								{logDialogEntries.length === 1 ? "access" : "accesses"} ·{" "}
								{dayjs(startDate).format("MMM D")} –{" "}
								{dayjs(endDate).format("MMM D, YYYY")}
							</div>
							<div className="accessLogsDashboardDialogHeaderRow">
								<div>Date &amp; time</div>
								<div>Notes</div>
							</div>
							<div className="accessLogsDashboardDialogList">
								{logDialogEntries.map((entry) => (
									<div className="accessLogsDashboardDialogRow" key={entry.id}>
										<div className="accessLogsDashboardDialogRowDate">
											{convertToPST(entry.created)}
										</div>
										<div className="accessLogsDashboardDialogRowNotes">
											{entry.notes || "—"}
										</div>
									</div>
								))}
								{logDialogEntries.length === 0 && (
									<div className="accessLogsDashboardDialogEmpty">
										No Results Found
									</div>
								)}
							</div>
						</DialogContent>
					</>
				)}
			</Dialog>
		</div>
	);
};

export default AccessLogsDashboard;
